/**
 * uploadAdapter.ts
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import { Editor } from "ckeditor5";
import bluebird from "bluebird";

import { authServiceFactory } from "../services/auth.service";
import { bufferToBase64 } from "../services/encoding.service";
import { groupServiceFactory } from "../services/group.service";

export interface UploadResult {
  default: string,
  [size: string]: string
}

export interface SCUploaderAdapterOptions {
  groupID: () => string,
  statusRenderer: (elem: HTMLElement, uploadStatus: "idle" | "uploading" | "complete" | "final" | "invalid", percent: number) => void,
  uploadEvent: (eventName: "start" | "complete" | "error" | "removed", uploadID: string, msg: string) => void
}

export function removeFileFromGroup(groupID: string, fileID: string): Promise<void> {
  const groupService = groupServiceFactory();
  const authService = authServiceFactory();
  if (!authService.currentAccountMembership) {
    throw new Error("No Current Account");
  }

  return groupService.deleteFile(authService.currentAccountMembership.accountID, groupID, fileID);
}

export class ScUploadAdapter {
  static MAX_IMG_SIZE = 10485760; // 10MB
  static MAX_DIM = 4096;

  private xhr: XMLHttpRequest | undefined;

  constructor (private loader: any, private options: SCUploaderAdapterOptions) {
  }

  async upload(): Promise<UploadResult> {
    const file: File = await this.loader.file;
    if (this.loader.nonImage) {
      return await this.doFileUpload(file, this.options.groupID());
    } else {
      const dataStr = file.size > ScUploadAdapter.MAX_IMG_SIZE ?
        await this.resizeImage(file) :
        `data:${file.type};base64,${bufferToBase64(await file.arrayBuffer())}`

      return {default: dataStr};
    }
  }

  async doFileUpload(file: File, groupID: string): Promise<any> {
    const loader = this.loader;
    const groupService = groupServiceFactory();
    const authService = authServiceFactory();
    if (!authService.currentAccountMembership) {
      throw new Error("No Current Account");
    }
    return await groupService.uploadFileToGroup(authService.currentAccountMembership.accountID,
      groupID, file, (loaded, total) => {
        loader.uploadTotal = total;
				loader.uploaded = loaded;
      }, xhr => { this.xhr = xhr;});
  }

  resizeImage(file: File): Promise<string> {
    return new bluebird<string>(resolve => {
      const image = new Image();
      image.onload = function () {
        const canvas = document.createElement("canvas");
        let width = image.width;
        let height = image.height;
        if (width > height) {
          if (width > ScUploadAdapter.MAX_DIM) {
              height *= ScUploadAdapter.MAX_DIM / width;
              width = ScUploadAdapter.MAX_DIM;
          }
        } else {
            if (height > ScUploadAdapter.MAX_DIM) {
                width *= ScUploadAdapter.MAX_DIM / height;
                height = ScUploadAdapter.MAX_DIM;
            }
        }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d")?.drawImage(image, 0, 0, width, height);
        resolve(canvas.toDataURL(file.type));
      };
      image.src = URL.createObjectURL(file);
    });
  }

  abort(): void {
    this.xhr?.abort();
  }
}

export function ScUploaderPlugin(editor: Editor): void {
  const options = editor.config.get("scUpload") as SCUploaderAdapterOptions;
  editor.plugins.get("FileRepository").createUploadAdapter = (loader: any) => {
    return new ScUploadAdapter(loader, options);
  };
}
